import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { RepairOperationDto, RepairOperationSearchPaginatedDto } from "@/core/api/generated/v1";

import ContractInline from "../Contract/ContractInline";
import VehicleInline from "../Vehicle/VehicleInline";

export type RepairOperationAutocompleteOption = BaseAutocompleteOption<RepairOperationDto>;

export const getRepairOperationOptionTitle = (data: RepairOperationDto): string =>
  StringHelper.join([data.name || data.localNumber], ", ", {
    skipEmpty: true,
  });

export const repairOperationToOption = (
  data: RepairOperationDto,
): RepairOperationAutocompleteOption => ({
  id: data.id!,
  title: getRepairOperationOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface RepairOperationAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<RepairOperationDto> {
  searchFilters?: Partial<Omit<RepairOperationSearchPaginatedDto, "search" | "includeIds">>;
}

export default function RepairOperationAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: RepairOperationAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={repairOperationToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.repairOperationsApi.apiV1RepairOperationsSearchPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          repairOperationSearchPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          repairOperationSearchPaginatedDto: {
            ...params.repairOperationSearchPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Repair operation'
      placeholder='Search...'
      renderOption={(props, option: RepairOperationAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='repairOperation' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option?.data?.vehicle && (
                        <VehicleInline entity={option.data.vehicle} withIcon={false} />
                      )}
                      {option?.data?.contract && (
                        <ContractInline entity={option.data.contract} withIcon={false} />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
